import type { ComponentProps, Context, ReactNode, UIEvent } from 'react';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { isScrollNearBottom } from '@gonfalon/dom/isScrollNearBottom';
import { isScrollNearTop } from '@gonfalon/dom/isScrollNearTop';
import { QueryErrorBoundary } from '@gonfalon/error-boundaries/QueryErrorBoundary';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import type {
  FetchNextPageOptions,
  InfiniteData,
  QueryKey,
  UseSuspenseInfiniteQueryOptions,
  UseSuspenseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';

type InfiniteSuspenseQueryProps<
  QueryFnData,
  Error,
  Data extends InfiniteData<QueryFnData, PageParam>,
  Key extends QueryKey,
  PageParam,
> = {
  context?: Context<Data>;
  infiniteQuery: UseSuspenseInfiniteQueryOptions<QueryFnData, Error, Data, QueryFnData, Key, PageParam>;
  onFetchStart?: () => void;
  onFetchEnd?: () => void;
  children: (props: UseSuspenseInfiniteQueryResult<Data, Error> & { onScroll: (event: UIEvent) => void }) => ReactNode;
  direction?: 'bottom' | 'top';
  fallback?: ReactNode;
} & (
  | {
      boundary: ComponentProps<typeof QueryErrorBoundary>['fallbackRender'];
      errorSeverity: ComponentProps<typeof QueryErrorBoundary>['severity'];
      owner: ComponentProps<typeof QueryErrorBoundary>['owner'];
      errorIgnoredStatuses: ComponentProps<typeof QueryErrorBoundary>['ignoredStatuses'];
    }
  | {
      boundary?: never;
      errorSeverity?: never;
      owner?: never;
      errorIgnoredStatuses?: never;
    }
);

/**
 * A wrapper component that combines React Suspense with TanStack Query's useSuspenseInfiniteQuery
 * to provide declarative infinite data fetching with built-in loading states and error boundaries.
 *
 * In addition to supplying the base query result object to the render prop, it also provides
 * an `onScroll` callback that automatically fetches the next page when scrolling near the bottom
 * (or top) of the list. The `onFetchStart` and `onFetchEnd` callbacks can be used to track
 * the loading state of subsequent page fetches externally.
 *
 * @example
 * ```tsx
 * <InfiniteSuspenseQuery
 *   infiniteQuery={{
 *     queryKey: ['projects', orgId],
 *     queryFn: ({ pageParam }) => fetchProjects(orgId, pageParam),
 *     initialPageParam: 0,
 *     getNextPageParam: (lastPage) => lastPage.nextCursor
 *   }}
 *   fallback={<Spinner />}
 *   boundary={({ error, resetErrorBoundary }) => (
 *     <div>
 *       <p>Failed to load projects: {error.message}</p>
 *       <button onClick={resetErrorBoundary}>Retry</button>
 *     </div>
 *   )}
 *   errorSeverity="medium"
 *   owner="platform-team"
 *   errorIgnoredStatuses={[404, 403]}
 *   direction="bottom"
 *   onFetchStart={() => setIsLoadingMore(true)}
 *   onFetchEnd={() => setIsLoadingMore(false)}
 * >
 *   {({ data, onScroll }) => (
 *     <div onScroll={onScroll}>
 *       {data.pages.flatMap(page => page.items).map(project => (
 *         <ProjectCard key={project.id} project={project} />
 *       ))}
 *     </div>
 *   )}
 * </InfiniteSuspenseQuery>
 * ```
 */
export function InfiniteSuspenseQuery<
  QueryFnData,
  Error,
  Data extends InfiniteData<QueryFnData, PageParam>,
  Key extends QueryKey,
  PageParam,
>({
  fallback,
  boundary,
  errorSeverity,
  owner,
  errorIgnoredStatuses,
  ...props
}: InfiniteSuspenseQueryProps<QueryFnData, Error, Data, Key, PageParam>) {
  const content =
    fallback !== undefined ? (
      <Suspense fallback={fallback}>
        <InfiniteSuspenseQueryContent {...props} />
      </Suspense>
    ) : (
      <InfiniteSuspenseQueryContent {...props} />
    );

  return boundary ? (
    <QueryErrorBoundary
      fallbackRender={boundary}
      severity={errorSeverity}
      owner={owner}
      ignoredStatuses={errorIgnoredStatuses}
    >
      {content}
    </QueryErrorBoundary>
  ) : (
    content
  );
}

function InfiniteSuspenseQueryContent<
  QueryFnData,
  Error,
  Data extends InfiniteData<QueryFnData, PageParam>,
  Key extends QueryKey,
  PageParam,
>({
  infiniteQuery,
  children,
  context: QueryContext,
  onFetchStart,
  onFetchEnd,
  direction = 'bottom',
}: Omit<
  InfiniteSuspenseQueryProps<QueryFnData, Error, Data, Key, PageParam>,
  'fallback' | 'boundary' | 'errorSeverity' | 'owner' | 'errorIgnoredStatuses'
>) {
  const queryResult = useSuspenseInfiniteQuery(infiniteQuery);

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const onFetchStartRef = useLatestRef(onFetchStart);
  const onFetchEndRef = useLatestRef(onFetchEnd);

  const fetchNextPage = useCallback(
    async (options?: FetchNextPageOptions) => {
      onFetchStartRef.current?.();
      try {
        return await queryResult.fetchNextPage(options);
      } finally {
        if (isMountedRef.current) {
          onFetchEndRef.current?.();
        }
      }
    },
    [queryResult, onFetchStartRef, onFetchEndRef],
  );

  const onScrollRef = useLatestRef((event: UIEvent) => {
    const isScrollAtTrigger =
      direction === 'bottom' ? isScrollNearBottom(event.nativeEvent) : isScrollNearTop(event.nativeEvent);
    if (isScrollAtTrigger && queryResult.hasNextPage && !queryResult.isFetchingNextPage) {
      void fetchNextPage();
    }
  });

  const onScroll = useCallback(
    (event: UIEvent) => {
      onScrollRef.current(event);
    },
    [onScrollRef],
  );

  return QueryContext ? (
    <QueryContext value={queryResult.data}>{children({ ...queryResult, fetchNextPage, onScroll })}</QueryContext>
  ) : (
    children({ ...queryResult, fetchNextPage, onScroll })
  );
}
